// ============================================
// document-mention-search.ts — Bug A fix (server-side @-mention search)
// ============================================
//
// The R16 picker only ever saw the tasks the parent had already
// loaded (`useTasks` → first page of 40) and then truncated that list
// to the first 30 BEFORE filtering. Any task beyond that window was
// impossible to mention — typing its title returned nothing.
//
// This module is the fix for the data side: query the project task
// endpoint with its own `search` parameter so the picker can find ANY
// task in the project:
//
//   GET /api/projects/:projectId/tasks?search=<query>&limit=30&offset=0
//
// The picker renders 30 task rows at a time and appends the next page
// (`offset += 30`) behind a "Load more" button, so a 435-task project
// is fully searchable while the popup stays compact.
//
// `useTasks` (and therefore the parent) keeps its own pagination — the
// picker no longer depends on it for task search. The endpoint is
// resolved through the shared `apiGet` helper so the API base URL
// (nginx `/api` proxy or `VITE_API_URL`) is never hardcoded here.
//
// The backend matches `search` against the task TITLE **or** the task
// DESCRIPTION (case-insensitive), which is why the component must not
// re-apply its own title-only substring filter to these results — a
// description match would be dropped again (that would be the same bug
// in a new place).

import { apiGet } from '@/lib/api';
import type { Task } from '@/lib/schemas';

/** Page size for the picker's task list — 30 rendered rows per page. */
export const MENTION_TASK_PAGE_SIZE = 30;

/**
 * Debounce window for the server query. ~220 ms keeps fast typing to a
 * single request while still feeling instant.
 */
export const MENTION_SEARCH_DEBOUNCE_MS = 220;

/** Shape returned by `GET /api/projects/:projectId/tasks`. */
export interface MentionTaskPage {
    tasks: Task[];
    total: number;
    offset: number;
    limit: number;
    hasMore: boolean;
}

export interface MentionTaskQueryOptions {
    /** Rows to request (defaults to `MENTION_TASK_PAGE_SIZE`). */
    limit?: number;
    /** Pagination offset — 0 for a fresh search, `items.length` to append. */
    offset?: number;
    /** Abort signal so stale requests can be cancelled. */
    signal?: AbortSignal;
}

/**
 * Build the project-tasks endpoint for the picker.
 *
 * - With a query: `?search=<query>&limit=30&offset=0` — the backend
 *   narrows the WHOLE project, not just a loaded page, so every task is
 *   reachable no matter how many exist.
 * - Without a query: `?limit=30&offset=0` — the same client-side
 *   pagination over the project's normal ordering
 *   (`sortBy=order&sortOrder=asc`).
 *
 * `offset` moves the window forward; the picker appends the extra rows
 * behind a "Load more" button.
 */
export function buildMentionTasksEndpoint(
    projectId: string,
    query: string,
    options?: { limit?: number; offset?: number }
): string {
    const params = new URLSearchParams();
    const trimmed = query.trim();
    if (trimmed) params.set('search', trimmed);
    // R25 (Bug A): the picker must search the PROJECT-WIDE list,
    // independent of the active saved view's filters/sort/pagination, and
    // it must see subtasks too — SAE5 has 59 root tasks + 376 subtasks,
    // and subtasks were previously unreachable in the picker.
    params.set('includeSubtasks', 'true');
    params.set('limit', String(options?.limit ?? MENTION_TASK_PAGE_SIZE));
    params.set('offset', String(options?.offset ?? 0));
    return `/projects/${encodeURIComponent(projectId)}/tasks?${params.toString()}`;
}

/**
 * Fetch one page of the picker's task list from the server. The caller
 * owns the debounce + `AbortSignal` so a stale response can never
 * overwrite a newer one.
 */
export async function searchMentionTasks(
    projectId: string,
    query: string,
    options?: MentionTaskQueryOptions
): Promise<MentionTaskPage> {
    return apiGet<MentionTaskPage>(
        buildMentionTasksEndpoint(projectId, query, options),
        { signal: options?.signal }
    );
}

/**
 * Endpoint used by the person-mention popover (Bug B): every task
 * assigned to one project member.
 */
export function buildAssigneeTasksEndpoint(
    projectId: string,
    userId: string,
    limit = 100
): string {
    const params = new URLSearchParams();
    params.set('assignee', userId);
    // A person can be assigned subtasks — include them so the popover
    // shows everything that person owns in this project.
    params.set('includeSubtasks', 'true');
    params.set('limit', String(limit));
    params.set('offset', '0');
    return `/projects/${encodeURIComponent(projectId)}/tasks?${params.toString()}`;
}

export async function fetchAssigneeTasks(
    projectId: string,
    userId: string,
    signal?: AbortSignal
): Promise<MentionTaskPage> {
    return apiGet<MentionTaskPage>(
        buildAssigneeTasksEndpoint(projectId, userId),
        { signal }
    );
}